import React, {useEffect, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import {useTranslation} from "react-i18next";

import alertIcon from "url:shared-assets/icons/red/alert.svg";
import badgeCheckIcon from "url:shared-assets/icons/green/badge-check.svg";
import calendarXIcon from "url:shared-assets/icons/calendar-x.svg";
import iconInfo from "url:shared-assets/icons/white/information-circle.svg";
import iconRight from "url:shared-assets/icons/chevronRight.svg";

import {Modal, ModalBody, ModalHeader} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import RadioButton from "../../components/RadioButton";
import Api from "../../services/Api";
import {getLanguage, languageMap} from "../../lang/helpers";
import {StrArr, StrMap} from "../../interfaces/common";
import {formatDate} from "../../helpers/dateFunctions";
import {FeedbackType} from "../SurveyResult/interfaces";

interface QuestionInterface {
  questionId: string;
  names: StrMap;
  noAnswer: boolean;
  freeText: boolean;
  answer: number;
  freeAnswer: string;
}

interface SectionInterface {
  names: StrMap;
  questions: Array<QuestionInterface>;
}

interface SurveyInterface {
  language: string;
  languages: string[];
  deadline: string;
  answerOptions: number;
  answerType: number;
  showNoAnswer: boolean;
  translations_answers: StrArr;
  names: StrMap;
  sections: Array<SectionInterface>
}

interface OtherSurveyInterface {
  id: string,
  name: string,
  deadline: string,
  token: string,
}

interface DataInterface {
  status: string;
  client_name: string;
  answered_count: number;
  deadline: string;
  nr_feedback_users: number;
  feedbackType: FeedbackType
}

const SurveyResponse = () => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(); // i18n translation hook
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DataInterface | null>(null);
  const [otherSurveys, setOtherSurveys] = useState<Array<OtherSurveyInterface>>([]);
  const [survey, setSurvey] = useState<SurveyInterface>({
    language: "en",
    languages: [],
    deadline: "",
    answerOptions: 5,
    answerType: 0,
    showNoAnswer: true,
    names: {},
    translations_answers: {},
    sections: []
  });
  const [showFeedbackSent, setShowFeedbackSent] = useState(false);
  const [language, setLanguage] = useState("en");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const { id } = useParams();
  // State to store user responses
  const [showAlert, setShowAlert] = useState(false);
  const [selfInfo, setSelfInfo] = useState(false);

  let url = "client/survey/" + id;
  if (token) {
    url += "/" + token;
  }

  const setDefaultLanguage = (_survey: SurveyInterface) => {
    if (_survey.languages.indexOf(lang) !== -1) {
      // If browser language is in survey.languages, use that language
      setLanguage(lang);
    } else {
      // Else use survey language
      setLanguage(_survey.language);
    }
  }

  useEffect(() => {
    const anonymous = !!token;
    api
      .get(url, [], anonymous)
      .then((response) => {
        if (response.json.status === "ok") {
          setData(response.json.data);
          setSurvey(response.json.survey);
          setDefaultLanguage(response.json.survey)
        } else {
          setError(response.json.error);
          setData(response.json);
        }
        setOtherSurveys(response.json.otherSurveys);
        setLoading(false);
      })
      .catch((err) => {
        alert("An error occurred: " + err);
      });
  }, []);


  const sendResponses = () => {
    const anonymous = !!token;
    api.post(url, survey, [], anonymous).then((response) => {
      if (response.json.status === "ok") {
        setShowFeedbackSent(true);
      }
    })
  }

  const formatError = () => {
    let errorHeader = "Unknown error: " + error;
    let errorBody =
      lang === "sv"
        ? "Kontakta support@zebrain.se så hjälper vi dig"
        : "Contact support@zebrain.se and we will help you";
    if (error === "deadline-passed" || error === "already-submitted") {
      errorHeader = t(error);
      if (token) {
        errorBody = t(error + "-message");
      } else {
        errorBody = lang === "sv" ? "Du har redan skickat din självskattning" :
          "You have already submitted your self-assessment"
      }

    } else if (error === "badToken") {
      errorHeader =
        lang === "sv"
          ? "Något är konstigt med din token"
          : "Something is wrong with your token";
    } else if (error === "badRecipient") {
      errorHeader =
        lang === "sv"
          ? "Du är inte registrerad mottagare"
          : "You are not a registered recipient";
    }
    return (
      <div className="mt-10 main-content">
        <div className="flex flex-col h-full flex-grow min-h-0 gap-6 mt-6">
          {error === "deadline-passed" && (
            <img
              src={calendarXIcon}
              alt="Calendar X icon"
              className="w-12 h-12 mx-auto"
            />
          )}
          <div className="flex flex-col gap-2">
            <h1 className="z-heading1 text-white text-center">
              {errorHeader}
            </h1>
            <div
              className="text-gray-400 text-center"
              dangerouslySetInnerHTML={{ __html: errorBody }}
            />
          </div>
          {
            (error === "already-submitted" && !token) &&
              <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: 250}}
                            onClick={() => navigate("/assessments")}>
                {lang === "sv" ? "Tillbaka till mätningar" : "Back to assessments"}
              </CustomButton>
          }
          {(error === "deadline-passed" || error === "already-submitted") &&
            renderOtherSurveys()}
        </div>
      </div>
    );
  };

  const renderOtherSurveys = () => {
    if (otherSurveys.length === 0) return null
    return (
      <div className="mt-6">
        <p className="font-medium">
          {lang === "sv"
            ? "Andra feedback-förfrågningar du inte har svarat på än"
            : "Other feedback requests you have not answered yet"}
          :
        </p>
        {otherSurveys.map((otherSurvey, index) => (
          <div
            className="mt-2 flex justify-between cursor-pointer bg-indigo-900 rounded-md p-4 hover:bg-indigo-800"
            key={"other_q_" + index}
            onClick={() =>
              (window.location.href =
                "/feedback/survey/" +
                otherSurvey.id +
                "?token=" +
                otherSurvey.token)
            }
          >
            <p className="w-40 truncate">{otherSurvey.name}</p>
            <p className="w-40 text-gray-400">
              Deadline {formatDate(otherSurvey.deadline, false, lang)}
            </p>
            <img src={iconRight} alt="Go" />
          </div>
        ))}
      </div>
    );
  }

  const renderSelfInfo = () => {
    return (
      <Modal isOpen={selfInfo} onClose={() => setSelfInfo(false)}>
        <ModalHeader onClose={() => setSelfInfo(false)}>
          <div className="z-title1">{t("self-assessment")}</div>
        </ModalHeader>
        <ModalBody>
          <div className="text-gray-400">{t("self-assessment-txt1")}</div>

          <div className="text-gray-400 mt-4">
            {t("self-assessment-txt2")}
          </div>
        </ModalBody>
      </Modal>
    );
  };

  if (error) {
    return formatError();
  }

  if (showFeedbackSent) {
    return (
      <div className="mt-10 main-content">
        <div className="flex flex-col h-full flex-grow min-h-0 gap-6 mt-6">
          <img
            src={badgeCheckIcon}
            alt="Badge check icon"
            className="w-12 h-12 mx-auto"
          />
          <div className="flex flex-col gap-2">
            <h1 className="z-heading1 text-white text-center">
              {(!token && data?.feedbackType !== FeedbackType.ONLY_SELF) ? (
                <>{lang === "sv" ? "Dags att be om feedback" : "Time to request feedback"}</>
              ) : (
                <>{token ? t("feedback-sent") : t("self-assessment-complete")}</>
              )}
            </h1>
            {!token ? (
              <>
                <p className="text-gray-400 text-center">
                  {
                    data?.feedbackType === FeedbackType.ONLY_SELF ?
                      lang === "sv" ? "Kolla in din insiktsrapport" :
                        "Check your insights report"
                      :
                      lang === "sv" ? "Få fler perspektiv som stärker och kompletterar din självskattning." :
                      "Get additional perspectives that strengthen and complement your self-assessment."
                  }
                </p>
                {
                  data?.feedbackType === FeedbackType.ONLY_SELF ? (
                      <CustomButton buttonType="blueBase" className="mt-1 mx-auto" style={{width: 250}}
                                  onClick={() => navigate("/dev-plan/survey/result/" + id + "/summary")}>
                        {t("view-your-insights-report")}
                      </CustomButton>

                  ) : (
                    <CustomButton buttonType="blueBase" className="mt-4 mx-auto" style={{width: 250}}
                                  onClick={() => navigate("/dev-plan/survey_request_invite/" + id)}>
                      {lang === "sv" ? "Välj mottagare" : "Select recipients"}
                    </CustomButton>
                  )
                }
              </>
            ) : (
              <div>
                <p className="text-gray-400 text-center">
                  {t("thank-you-for-your-feedback")}
                </p>
                {renderOtherSurveys()}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading || data === null) {
    return null;
  }

  const updateQuestion = (questionId: string, value: string, what: string) => {
    const _data = {...data}
    for (const _section of survey.sections) {
      for (const _question of _section.questions) {
        if (_question.questionId === questionId) {
          if (what === "a") {
            // console.log(_question.questionId + ' === ' + questionId + ' = ' + value);
            _question.answer = parseInt(value);
          } else if (what === "text") {
            _question.freeAnswer = value;
          }
          break;
        }
      }
    }
    setData(_data);
  }

  const surveyNotComplete = () => {
    for (const section of survey.sections) {
      for (const question of section.questions) {
        if (question.answer < 0) {
          return true;
        }
      }
    }
    return false;
  }

  const formatQuestion = (question: QuestionInterface) => {

    let answerOptions = [];
    if (survey.answerOptions > 7) {
      for (let i = 1; i <= survey.answerOptions; i++) {
        answerOptions.push({
          name: i + "",
          value: i + ""
        })
      }
    } else {
      for (let i = survey.answerOptions; i > 0; i--) {
        answerOptions.push({
          name: survey.translations_answers[language][i],
          value: i + ""
        })
      }
      // Do not add noAnswer for self-assessments
      if (token && survey.showNoAnswer) {
        answerOptions.push({
          name: survey.translations_answers[language][0],
          value: "0"
        })
      }
    }
    let questionText = question.names[language];
    if (questionText.startsWith("X ")) {
      questionText = data.client_name + " " + questionText.substring(2);
    }
    return (
      <>
        <p className="font-medium mb-4">{questionText}</p>
        {
          survey.answerOptions > 7 ? (
            <>
              <div className="flex justify-between">
                {
                  answerOptions.map((a, i) => {
                    return (
                      <RadioButton id={"A " + i} key={"a_" + question.questionId + "_" + i} value={i + ""}
                                   name={"A " + i} label={a.value}
                                   onChange={() => updateQuestion(question.questionId, a.value, "a") }
                                   checked={question.answer === parseInt(a.value)}/>
                    )
                  })
                }
              </div>
              <div className="flex justify-between mt-2">
                <p>1 = {survey.translations_answers[language][1]}</p>
                <p>10 = {survey.translations_answers[language][10]}</p>
              </div>
            </>
          ) : (
            <div>
              {
                answerOptions.map((a, i) => {
                  const _id = "a_" + question.questionId + "_" + i
                  return (
                    <div key={_id} className="mt-2">
                      <RadioButton id={_id} value={a.value}
                                   name={_id} label={a.name}
                                   onChange={() => updateQuestion(question.questionId, a.value, "a") }
                                   checked={question.answer === parseInt(a.value)}/>
                    </div>
                  )
                })
              }
            </div>
          )
        }
        {
          (question.freeText && token) &&
          <div className="mt-4">
              <textarea className="z-input" value={question.freeAnswer} placeholder="Free answer"
                        onChange={(e) => updateQuestion(question.questionId, e.target.value, "text")}></textarea>
          </div>
        }
        <hr className="my-4 border-indigo-800" />
      </>
    )
  }

  const changeLanguage = (l: string)  => {
    if (survey.languages.indexOf(l) !== -1) {
      setLanguage(l);
    }
    if (l === 'en' || l === 'sv') {
      i18n.changeLanguage(l).then();
    }
  }

  const showChangeLanguage = () => {
    if (survey.languages.length < 2) return null;
    return (
      <div className="flex mt-2 gap-x-4 items-center">
        {
          survey.languages.map((l, i) => {
            return (
              <div className={language === l ? "underline" : "cursor-pointer"} key={"lang_" + i}
                   onClick={() => changeLanguage(l)}>
                {languageMap[l]}
              </div>
            )
          })
        }
      </div>
    )
  }

  return (
    <div className="main-content">
      {selfInfo && renderSelfInfo()}
      {showChangeLanguage()}
      <h1 className="z-heading1 text-white mt-4">
        {!token ? (
          <div className="flex items-center">
            <div>{t("complete-self-assessment")}</div>
            <div className="ml-2 p-1 flex item-center justify-center cursor-pointer  rounded-[20px] 
            hover:bg-indigo-700" onClick={() => setSelfInfo(true)}><img
              src={iconInfo}
              alt="info"/></div>
          </div>
        ) : (
          <>
            {t("asking-for-feedback")} {data.client_name}
          </>
        )}
      </h1>

      {token && (
        <>
          <div className="text-gray-400">
            <ul className="list-disc list-inside">
              <li>
                {data.answered_count}
                {lang === "sv" ? " av " : " out of "}
                {data.nr_feedback_users} {t("feedback-number-of-feedbacks")}
              </li>

              {data.nr_feedback_users > 2 && (
                <li>{t("feedback-is-anonymous")}</li>
              )}
            </ul>
          </div>
          <hr className="border-indigo-700 mt-4" />
        </>
      )}

      <div>
        {
          survey.sections.map((section, i) => {
            return (
              <div key={"section_" + i} className="mt-4">
                <p className="text-2xl mb-4">{section.names[language]}</p>
                {
                  section.questions.map(question => {
                    return (
                      <div className="mt-4" key={"question_" + question.questionId}>
                        {formatQuestion(question)}
                      </div>
                    )
                  })
                }
              </div>
            )
          })
        }
      </div>

      {showAlert && (
        <div className="flex items-center rounded-md border border-cherry-500 bg-cherry-200 text-indigo-1000 p-4 gap-2">
          <img src={alertIcon} alt="Alert" className="w-6 h-6" />
          <span>{t("feedback-response-form-alert")}</span>
        </div>
      )}
      <CustomButton disabled={surveyNotComplete()} onClick={() => sendResponses()} className="mt-4">
        {t("submit")}
      </CustomButton>
      <div className="h-20">&nbsp;</div>
    </div>
  );
};

export default SurveyResponse;
